import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// --- Standalone Analytics Functions ---
// These can be imported and used in any file (components, stores, etc.)

// Base event tracker
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

// Authentication Events
export const trackAuth = {
  login: (method) => trackEvent('login', { method }),
  signup: (method) => trackEvent('signup', { method }),
  logout: () => trackEvent('logout'),
  passwordReset: () => trackEvent('password_reset'),
  emailVerification: () => trackEvent('email_verification')
};

// Video Generation Events
export const trackVideo = {
  start: (type, duration) => trackEvent('video_generation_start', { type, duration }),
  complete: (type, duration, videoId) => trackEvent('video_generation_complete', { type, duration, videoId }),
  error: (type, error) => trackEvent('video_generation_error', { type, error }),
  download: (videoId, type) => trackEvent('video_download', { videoId, type }),
  share: (videoId, platform) => trackEvent('video_share', { videoId, platform })
};

// Image Generation Events
export const trackImage = {
  start: (type) => trackEvent('image_generation_start', { type }),
  complete: (type, imageId) => trackEvent('image_generation_complete', { type, imageId }),
  error: (type, error) => trackEvent('image_generation_error', { type, error }),
  download: (imageId) => trackEvent('image_download', { imageId }),
  share: (imageId, platform) => trackEvent('image_share', { imageId, platform })
};

// Sound Effects Events
export const trackSound = {
  start: (type) => trackEvent('sound_generation_start', { type }),
  complete: (type, soundId) => trackEvent('sound_generation_complete', { type, soundId }),
  error: (type, error) => trackEvent('sound_generation_error', { type, error }),
  download: (soundId) => trackEvent('sound_download', { soundId }),
  share: (soundId, platform) => trackEvent('sound_share', { soundId, platform })
};

// User Interaction Events
export const trackInteraction = {
  buttonClick: (buttonName, page) => trackEvent('button_click', { buttonName, page }),
  formSubmit: (formName, success) => trackEvent('form_submit', { formName, success }),
  search: (query) => trackEvent('search', { query }),
  filter: (filterType, value) => trackEvent('filter', { filterType, value }),
  sort: (sortType) => trackEvent('sort', { sortType })
};

// Feature Usage Events
export const trackFeature = {
  use: (featureName) => trackEvent('feature_use', { featureName }),
  explore: (featureName) => trackEvent('feature_explore', { featureName }),
  tutorial: (featureName) => trackEvent('feature_tutorial', { featureName })
};

// Error Tracking
export const trackError = {
  general: (errorType, message) => trackEvent('error', { type: errorType, message }),
  api: (endpoint, status) => trackEvent('api_error', { endpoint, status }),
  validation: (field, message) => trackEvent('validation_error', { field, message })
};

// User Flow Events
export const trackFlow = {
  start: (flowName) => trackEvent('flow_start', { flowName }),
  step: (flowName, stepNumber, stepName) => trackEvent('flow_step', { flowName, stepNumber, stepName }),
  complete: (flowName) => trackEvent('flow_complete', { flowName }),
  abandon: (flowName, stepNumber) => trackEvent('flow_abandon', { flowName, stepNumber })
};

// Subscription & Payment Events
export const trackSubscription = {
  viewPlan: (planId) => trackEvent('view_plan', { planId }),
  startSubscription: (planId) => trackEvent('start_subscription', { planId }),
  completeSubscription: (planId, amount) => trackEvent('complete_subscription', { planId, amount }),
  cancelSubscription: (planId) => trackEvent('cancel_subscription', { planId }),
  upgradePlan: (fromPlan, toPlan) => trackEvent('upgrade_plan', { fromPlan, toPlan }),
  downgradePlan: (fromPlan, toPlan) => trackEvent('downgrade_plan', { fromPlan, toPlan })
};

// Library Events
export const trackLibrary = {
  view: (itemType) => trackEvent('library_view', { itemType }),
  organize: (action, itemType) => trackEvent('library_organize', { action, itemType }),
  delete: (itemType, itemId) => trackEvent('library_delete', { itemType, itemId }),
  export: (itemType, itemId) => trackEvent('library_export', { itemType, itemId })
};


// --- React Hook for Components ---
// This hook uses the standalone functions and adds React-specific logic (like page views).
export const useAnalytics = () => {
  const location = useLocation();

  // Track page views automatically
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-LGM8CLDGRP', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);

  // Return all trackers for components to use
  return {
    trackEvent,
    trackAuth,
    trackVideo,
    trackImage,
    trackSound,
    trackInteraction,
    trackFeature,
    trackError,
    trackFlow,
    trackSubscription,
    trackLibrary
  };
};

export default useAnalytics; 